/**
 * xr_headsets — which headset this is, and the settings it starts a VR session with (XR P1.7 / P3.17, 2026-09-28).
 * Pure: no three, no DOM (the caller passes the user agent and the platform facts in).
 *
 * WHY A TABLE, NOT ONE NUMBER. The same "high" Atlas preset is fine on a desktop GPU and an order of magnitude over
 * budget on a standalone headset, and the standalone headsets differ by 2× in GPU and 4× in panel pixels. The rule
 * behind the table: framebuffer scale² × eye-buffer pixels ÷ GPU ≈ Quest 3 at its field-tested 0.75. The CPU side
 * (draw calls, JavaScript) is the same on every mobile headset, so the VR level does not scale with the GPU alone.
 * Every value is a STARTING POINT to verify on the device (OVR Metrics Tool / the in-headset Perf page) — none of
 * these were measured on hardware in this repo.
 *
 * DETECTION (researched 2026-09-28): the Quest browser's UA names the model in parentheses ("…; Quest 3) …
 * OculusBrowser/40.2 … VR Safari" — Meta's browser spec page, user-agents.net / useragents.io captures); Quest 3S and
 * Pico strings are not documented anywhere reliable, so a Quest UA without a known model falls back to the
 * EYE-BUFFER size the session reports (`refineByEyeBuffer`). Vision Pro's Safari reports a Mac UA ("Macintosh") —
 * WebKit's advice is to detect by capability, so "a Mac UA inside an immersive session with touch points" is the
 * visionOS signal (Mac Safari has no WebXR). Android XR (Galaxy XR, SM-I610) runs Chrome for Android; an Android UA
 * that is not a Quest or a Pico, inside an immersive session, is taken as Android XR. A desktop OS UA is PC VR
 * (Chrome/Edge over OpenXR: Link, Air Link, SteamVR).
 * Sources: developers.meta.com/horizon/documentation/web/browser-specs; webkit.org/blog/15162 (natural input);
 * dev.to "Detecting visionOS by JavaScript"; developer.android.com/develop/xr/web; en.wikipedia.org/wiki/Samsung_Galaxy_XR.
 */

import type { VrLevel } from './xr_profile.js';

export type HeadsetId =
  | 'quest2' | 'quest3' | 'quest3s' | 'questpro' | 'quest'
  | 'pico4ultra' | 'pico'
  | 'galaxyxr' | 'androidxr'
  | 'visionpro'
  | 'pcvr'
  | 'unknown';

export interface HeadsetProfile {
  id: HeadsetId;
  label: string;
  /** Eye-buffer scale handed to XRWebGLLayer before setSession (1 = the runtime's recommended size). */
  framebufferScale: number;
  /** Fixed foveation 0..1 (0 = off). Vision Pro foveates itself — 0 there. */
  foveation: number;
  /** The frame rate asked for first (the session may not offer it; `pickFrameRate` chooses). */
  targetHz: number;
  /** The VR quality level the session starts at (xr_profile: 0 quality · 1 balanced · 2 performance). */
  level: VrLevel;
  /** A one-line hardware note for the Perf page. */
  gpu: string;
}

/**
 * The starting table. Anchored on the one FIELD-TESTED value — Quest 3 at 0.75 (2026-08-16, "lag is still bad" at
 * 0.9) — and scaled so every standalone headset shades about the same eye-buffer pixels per unit of GPU:
 * scale = 0.75 × √(Quest 3's recommended eye pixels ÷ this headset's × this GPU ÷ Quest 3's). Quest 2 / Pro have
 * about half the GPU; 3S a Quest 2-class buffer on Quest 3's GPU; Galaxy XR ~3× the pixels on ~1.15× the GPU.
 */
export const HEADSETS: Readonly<Record<HeadsetId, Readonly<HeadsetProfile>>> = Object.freeze({
  quest2:     { id: 'quest2',     label: 'Meta Quest 2',       framebufferScale: 0.65, foveation: 1.0,  targetHz: 72, level: 2, gpu: 'XR2 Gen 1 · Adreno 650 (~½ Quest 3)' },
  quest3:     { id: 'quest3',     label: 'Meta Quest 3',       framebufferScale: 0.75, foveation: 0.67, targetHz: 72, level: 1, gpu: 'XR2 Gen 2 · Adreno 740' },
  quest3s:    { id: 'quest3s',    label: 'Meta Quest 3S',      framebufferScale: 0.85, foveation: 0.67, targetHz: 72, level: 1, gpu: 'XR2 Gen 2 · Adreno 740 · Quest 2-class panel' },
  questpro:   { id: 'questpro',   label: 'Meta Quest Pro',     framebufferScale: 0.6,  foveation: 1.0,  targetHz: 72, level: 2, gpu: 'XR2+ Gen 1 · Adreno 650 (~½ Quest 3)' },
  quest:      { id: 'quest',      label: 'Meta Quest',         framebufferScale: 0.7,  foveation: 1.0,  targetHz: 72, level: 2, gpu: 'unknown Quest model — cautious defaults' },
  pico4ultra: { id: 'pico4ultra', label: 'PICO 4 Ultra',       framebufferScale: 0.7,  foveation: 0.67, targetHz: 72, level: 1, gpu: 'XR2 Gen 2' },
  pico:       { id: 'pico',       label: 'PICO headset',       framebufferScale: 0.7,  foveation: 1.0,  targetHz: 72, level: 2, gpu: 'unknown PICO model — cautious defaults' },
  galaxyxr:   { id: 'galaxyxr',   label: 'Samsung Galaxy XR',  framebufferScale: 0.5,  foveation: 0.67, targetHz: 72, level: 1, gpu: 'XR2+ Gen 2 (~1.15× Quest 3) · 3552×3840 per eye' },
  androidxr:  { id: 'androidxr',  label: 'Android XR headset', framebufferScale: 0.5,  foveation: 0.67, targetHz: 72, level: 1, gpu: 'Android XR — assumed Galaxy XR-class' },
  visionpro:  { id: 'visionpro',  label: 'Apple Vision Pro',   framebufferScale: 0.7,  foveation: 0,    targetHz: 90, level: 0, gpu: 'Apple M-series · 3660×3200 per eye · the OS foveates' },
  pcvr:       { id: 'pcvr',       label: 'PC VR',              framebufferScale: 1.0,  foveation: 0,    targetHz: 90, level: 0, gpu: 'desktop GPU (Link / SteamVR / OpenXR)' },
  unknown:    { id: 'unknown',    label: 'VR headset',         framebufferScale: 0.7,  foveation: 1.0,  targetHz: 72, level: 2, gpu: 'unknown — cautious defaults' },
});

export interface HeadsetFacts {
  userAgent: string;
  /** navigator.maxTouchPoints (visionOS reports touch points under a Mac UA). */
  maxTouchPoints?: number;
  /** True when this is called for an immersive session (or navigator.xr answered immersive-vr). */
  immersive?: boolean;
}

/** Name the headset from the UA and the platform facts. Never throws; 'unknown' when nothing matches. */
export function detectHeadset(f: HeadsetFacts): HeadsetId {
  const ua = typeof f.userAgent === 'string' ? f.userAgent : '';
  if (/OculusBrowser|\bQuest\b/i.test(ua)) {
    // The model sits in the UA's parentheses. 3S before 3 (a prefix), Pro before the bare name.
    if (/Quest\s*3S\b/i.test(ua)) return 'quest3s';
    if (/Quest\s*3\b/i.test(ua)) return 'quest3';
    if (/Quest\s*Pro\b/i.test(ua)) return 'questpro';
    if (/Quest\s*2\b/i.test(ua)) return 'quest2';
    return 'quest';
  }
  if (/Pico/i.test(ua)) return /Ultra|A9210/i.test(ua) ? 'pico4ultra' : 'pico';
  if (/SM-I610/i.test(ua)) return 'galaxyxr';
  if (/Android/i.test(ua)) return f.immersive ? 'androidxr' : 'unknown';
  // visionOS Safari reports a Mac UA; a Mac has no WebXR, so a Mac UA in an immersive session with touch is visionOS.
  if (/Macintosh/i.test(ua) && (f.maxTouchPoints ?? 0) > 0 && f.immersive) return 'visionpro';
  if (/Windows NT|X11; Linux|CrOS|Macintosh/i.test(ua) && !/Mobile/i.test(ua)) return 'pcvr';
  return 'unknown';
}

/**
 * Refine an unnamed Quest by the per-eye buffer the session hands back (width, height of ONE eye at scale 1.0 —
 * the caller divides the XRWebGLLayer framebuffer by 2 and by the scale it asked for). Quest 3 recommends
 * 1680×1760; Quest 2 1440×1584; Quest Pro 1800×1920. Any other id comes back unchanged.
 */
export function refineByEyeBuffer(id: HeadsetId, eyeW: number, eyeH: number): HeadsetId {
  if (id !== 'quest' || !(eyeW > 0) || !(eyeH > 0)) return id;
  const near = (w: number, h: number): boolean => Math.abs(eyeW - w) / w < 0.06 && Math.abs(eyeH - h) / h < 0.06;
  if (near(1680, 1760)) return 'quest3';
  if (near(1800, 1920)) return 'questpro';
  if (near(1440, 1584)) return 'quest2';
  return id;
}

/** The starting profile for a headset, with any URL overrides already applied by the caller. */
export function headsetProfile(id: HeadsetId): HeadsetProfile {
  return { ...(HEADSETS[id] ?? HEADSETS.unknown) };
}

/**
 * Choose the frame rate to ask for: the highest supported rate at or under `want`, else the lowest supported;
 * null when the session reports none (the runtime keeps its own default). Rates are rounded (Quest reports 72.0).
 */
export function pickFrameRate(supported: ArrayLike<number> | null | undefined, want: number): number | null {
  if (!supported || !supported.length || !(want > 0)) return null;
  const rates = Array.from(supported).map((r) => Math.round(r)).filter((r) => r > 0).sort((a, b) => a - b);
  if (!rates.length) return null;
  let best: number | null = null;
  for (const r of rates) if (r <= want + 0.5) best = r;
  return best ?? rates[0];
}

/** True for a standalone headset (its own mobile GPU) — false for PC VR, where the desktop GPU draws. */
export function isStandalone(id: HeadsetId): boolean {
  return id !== 'pcvr';
}
